import { openDB } from 'idb';
import type { DBSchema, IDBPDatabase } from 'idb';
import type { Instrument } from './types';

export interface QueuedCapture {
  captureId: string;
  uid: string;
  projectId: string;
  instrument: Instrument;
  blob: Blob;
  thumbBlob: Blob | null;
  createdAt: number;
  attempts: number;
  lastError: string | null;
}

interface BlisxDB extends DBSchema {
  queue: {
    key: string;
    value: QueuedCapture;
    indexes: { 'by-createdAt': number };
  };
}

let dbPromise: Promise<IDBPDatabase<BlisxDB>> | null = null;

function getDB() {
  if (!dbPromise) {
    dbPromise = openDB<BlisxDB>('blisxbench', 1, {
      upgrade(db) {
        const store = db.createObjectStore('queue', { keyPath: 'captureId' });
        store.createIndex('by-createdAt', 'createdAt');
      },
    });
  }
  return dbPromise;
}

const listeners = new Set<() => void>();

/** Subscribe to queue-depth changes so the sync indicator stays honest. */
export function onQueueChange(fn: () => void) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notify() {
  listeners.forEach((fn) => fn());
}

export async function enqueue(item: Omit<QueuedCapture, 'attempts' | 'lastError'>) {
  const db = await getDB();
  await db.put('queue', { ...item, attempts: 0, lastError: null });
  notify();
}

/** Oldest first — a lab session replays in the order it happened. */
export async function listQueued(): Promise<QueuedCapture[]> {
  const db = await getDB();
  return db.getAllFromIndex('queue', 'by-createdAt');
}

export async function queueCount(): Promise<number> {
  const db = await getDB();
  return db.count('queue');
}

export async function dequeue(captureId: string) {
  const db = await getDB();
  await db.delete('queue', captureId);
  notify();
}

export async function markAttempt(captureId: string, error: string | null) {
  const db = await getDB();
  const existing = await db.get('queue', captureId);
  if (!existing) return;
  await db.put('queue', { ...existing, attempts: existing.attempts + 1, lastError: error });
  notify();
}

const LAST_SYNC_KEY = 'blisxbench:lastSync';

export function markSynced() {
  try {
    localStorage.setItem(LAST_SYNC_KEY, String(Date.now()));
  } catch {
    // Private mode or blocked storage — the indicator degrades, nothing else.
  }
  notify();
}

export function lastSyncedAt(): Date | null {
  try {
    const raw = localStorage.getItem(LAST_SYNC_KEY);
    return raw ? new Date(Number(raw)) : null;
  } catch {
    return null;
  }
}
